import {
  type AnchorSortBy,
  AnchorsFilterBar,
} from '@agentic-flow-studio/shared/components/anchors/AnchorsFilterBar';
import { SharedAnchorPage } from '@agentic-flow-studio/shared/components/anchors/SharedAnchorPage';
import { AddAnchorDialog } from '@agentic-flow-studio/shared/components/contacts/AddAnchorDialog';
import { useRecordRecentPage } from '@agentic-flow-studio/shared/hooks/useRecordRecentPage';
import { useSearchPreservingNavigate } from '@agentic-flow-studio/shared/hooks/useSearchPreservingNavigate';
import type { RouterOutputs } from '@agentic-flow-studio/shared/lib/trpc';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@agentic-flow-studio/shared/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@agentic-flow-studio/shared/ui/table';
import {
  Anchor as AnchorIcon,
  ArrowLeft,
  ExternalLink,
  Loader2,
  MoreVertical,
  Plus,
  Trash2,
  User,
} from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '@/utils/trpc';

type AnchorRow = RouterOutputs['anchors']['list']['items'][number];

export default function AnchorsPage() {
  const navigate = useSearchPreservingNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const [isAddAnchorOpen, setIsAddAnchorOpen] = React.useState(false);

  // Parse anchor ID from location pathname
  const anchorMatch = location.pathname.match(/\/anchors\/(\d+)/);
  const parsedAnchorId = anchorMatch ? parseInt(anchorMatch[1], 10) : undefined;

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  // Anchor detail query (only enabled when viewing a single anchor)
  const anchorDetailQuery = trpc.anchors.getById.useQuery(
    { id: parsedAnchorId! },
    { enabled: parsedAnchorId !== undefined }
  );

  // Anchors list query
  const anchorsQuery = trpc.anchors.list.useQuery();
  const anchors = anchorsQuery.data?.items || [];
  const isLoading = anchorsQuery.isLoading;

  // Contacts list for filter dropdown
  const contactsQuery = trpc.contacts.search.useQuery({ limit: 100 });
  const contacts = React.useMemo(
    () =>
      contactsQuery.data?.items?.map((c) => ({
        id: c.id,
        label: c.displayName || c.label,
      })) || [],
    [contactsQuery.data?.items]
  );

  const utils = trpc.useUtils();

  const deleteMutation = trpc.anchors.delete.useMutation({
    onSuccess: () => {
      toast.success('Anchor deleted');
      utils.anchors.list.invalidate();
      if (parsedAnchorId) {
        navigate(`/org/${orgId}/workspace/${workspaceId}/anchors`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const createAnchorMutation = trpc.anchors.create.useMutation({
    onSuccess: (newAnchor) => {
      toast.success('Anchor created');
      setIsAddAnchorOpen(false);
      utils.anchors.list.invalidate();
      navigate(`/org/${orgId}/workspace/${workspaceId}/anchors/${newAnchor.id}`);
    },
    onError: (error) => {
      toast.error(`Failed to create anchor: ${error.message}`);
    },
  });

  const handleDelete = (anchor: AnchorRow) => {
    if (isReadOnly) return;
    if (confirm(`Delete anchor "${anchor.label}"? This action cannot be undone.`)) {
      deleteMutation.mutate({ id: anchor.id });
    }
  };

  // Record recent page visit
  const viewedAnchor = anchorDetailQuery.data;
  useRecordRecentPage(
    viewedAnchor && parsedAnchorId !== undefined
      ? {
          key: `anchor:${viewedAnchor.id}`,
          type: 'anchor',
          label: viewedAnchor.label || viewedAnchor.domain,
          description: viewedAnchor.url,
        }
      : null
  );

  // ── URL Filter State for Directory View ──────────────────────────────────
  const searchQuery = searchParams.get('q') ?? '';
  const domainFilter = searchParams.get('domain') ?? 'all';
  const contactFilter = searchParams.get('contact') ?? 'all';
  const sortBy = (searchParams.get('sort') as AnchorSortBy) ?? 'updated';

  const setParam = React.useCallback(
    (key: string, value: string | null) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value === null || value === '' || value === 'all') {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setSearchQuery = React.useCallback((q: string) => setParam('q', q || null), [setParam]);
  const setDomainFilter = React.useCallback(
    (domain: string) => setParam('domain', domain),
    [setParam]
  );
  const setContactFilter = React.useCallback(
    (contact: string) => setParam('contact', contact),
    [setParam]
  );
  const setSortBy = React.useCallback((sort: AnchorSortBy) => setParam('sort', sort), [setParam]);

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    domainFilter !== 'all' ||
    contactFilter !== 'all' ||
    sortBy !== 'updated';

  const resetFilters = React.useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    for (const key of ['q', 'domain', 'contact', 'sort']) {
      nextParams.delete(key);
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Unique domains list for filter dropdown
  const domains = React.useMemo(() => {
    const set = new Set<string>();
    for (const a of anchors) {
      if (a.domain) set.add(a.domain);
    }
    return Array.from(set).sort();
  }, [anchors]);

  // Filtered & sorted anchors
  const filteredAnchors = React.useMemo(() => {
    return anchors
      .filter((anchor) => {
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchLabel = anchor.label?.toLowerCase().includes(q);
          const matchDomain = anchor.domain?.toLowerCase().includes(q);
          const matchUrl = anchor.url?.toLowerCase().includes(q);
          const matchContact = anchor.contactName?.toLowerCase().includes(q);
          if (!matchLabel && !matchDomain && !matchUrl && !matchContact) return false;
        }

        if (domainFilter !== 'all' && anchor.domain !== domainFilter) {
          return false;
        }

        if (contactFilter !== 'all') {
          if (contactFilter === 'linked') {
            if (anchor.contactId === null || anchor.contactId === undefined) return false;
          } else if (contactFilter === 'unlinked') {
            if (anchor.contactId !== null && anchor.contactId !== undefined) return false;
          } else {
            if (String(anchor.contactId) !== contactFilter) return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'created') {
          return b.createdAt - a.createdAt;
        }
        if (sortBy === 'alpha') {
          const nameA = (a.label || a.domain).toLowerCase();
          const nameB = (b.label || b.domain).toLowerCase();
          return nameA.localeCompare(nameB);
        }
        // Default: 'updated'
        return b.updatedAt - a.updatedAt;
      });
  }, [anchors, searchQuery, domainFilter, contactFilter, sortBy]);

  // ── Detail View Mode (/anchors/:anchorId) ─────────────────────────────────
  if (parsedAnchorId !== undefined) {
    if (anchorDetailQuery.isLoading) {
      return (
        <div className="flex h-full w-full items-center justify-center p-8 bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (!anchorDetailQuery.data) {
      return (
        <div className="flex flex-col h-full w-full bg-background">
          <div className="px-6 py-2.5 border-b border-border bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/org/${orgId}/workspace/${workspaceId}/anchors`)}
              className="text-muted-foreground hover:text-foreground hover:bg-accent gap-2 text-xs font-medium"
            >
              <ArrowLeft className="w-4 h-4 text-primary" />
              Back to Anchors Directory
            </Button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <AnchorIcon className="h-10 w-10 text-muted-foreground opacity-20 mb-3" />
            <h2 className="text-lg font-semibold">Anchor not found</h2>
            <p className="text-sm text-muted-foreground mt-1">
              This anchor may have been deleted or moved to another workspace.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full w-full bg-background overflow-hidden">
        {/* Top Breadcrumb Navigation */}
        <div className="px-6 py-2.5 border-b border-border bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/org/${orgId}/workspace/${workspaceId}/anchors`)}
            className="text-muted-foreground hover:text-foreground hover:bg-accent gap-2 text-xs font-medium"
          >
            <ArrowLeft className="w-4 h-4 text-primary" />
            Back to Anchors Directory
          </Button>
        </div>

        {/* Dedicated Anchor View with Expansive Side Panel */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <SharedAnchorPage
            anchor={anchorDetailQuery.data}
            workspaceId={workspaceId!}
            orgId={orgId!}
            onBack={() => navigate(`/org/${orgId}/workspace/${workspaceId}/anchors`)}
            onDelete={() => handleDelete(anchorDetailQuery.data)}
            onNavigateToTask={(taskId) => {
              navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/${taskId}`);
            }}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    );
  }

  // ── Directory List View (/anchors) ─────────────────────────────────────────
  return (
    <main className="flex-1 p-6 lg:p-10 space-y-6 bg-slate-50/50 dark:bg-slate-950/50 overflow-y-auto h-full scrollbar-thin">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Anchors</h1>
          <p className="text-muted-foreground mt-1">
            Every URL/domain anchor tracked across this workspace and the contact it belongs to.
          </p>
        </div>
        {!isReadOnly && (
          <Button onClick={() => setIsAddAnchorOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Track Anchor
          </Button>
        )}
      </div>

      <AddAnchorDialog
        open={isAddAnchorOpen}
        onOpenChange={setIsAddAnchorOpen}
        isSubmitting={createAnchorMutation.isPending}
        onSubmit={(data) => {
          createAnchorMutation.mutate({
            url: data.url,
            domain: data.domain,
            label: data.label,
          });
        }}
      />

      {/* Filter Bar */}
      <AnchorsFilterBar
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        domainFilter={domainFilter}
        onDomainFilterChange={setDomainFilter}
        contactFilter={contactFilter}
        onContactFilterChange={setContactFilter}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        domains={domains}
        contacts={contacts}
        hasActiveFilters={hasActiveFilters}
        onResetFilters={resetFilters}
      />

      {/* Directory Table */}
      <div className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[180px]">Linked Contact</TableHead>
              <TableHead className="w-[240px]">Label</TableHead>
              <TableHead>URL</TableHead>
              {!isReadOnly && <TableHead className="w-[70px] text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => (
                  <TableRow key={i}>
                    {Array(isReadOnly ? 3 : 4)
                      .fill(0)
                      .map((__, j) => (
                        <TableCell key={j}>
                          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                        </TableCell>
                      ))}
                  </TableRow>
                ))
            ) : filteredAnchors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isReadOnly ? 3 : 4} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <AnchorIcon className="h-10 w-10 text-muted-foreground opacity-20" />
                    <p className="text-sm text-muted-foreground">
                      {hasActiveFilters
                        ? 'No anchors match your filters'
                        : 'No anchors tracked yet'}
                    </p>
                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs">
                        Clear all filters
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredAnchors.map((anchor) => (
                <TableRow
                  key={anchor.id}
                  onClick={() =>
                    navigate(`/org/${orgId}/workspace/${workspaceId}/anchors/${anchor.id}`)
                  }
                  className="group transition-colors cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="w-[180px] max-w-[200px]">
                    {anchor.contactName ? (
                      <Link
                        to={`/org/${orgId}/workspace/${workspaceId}/contacts/${anchor.contactId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline text-xs flex items-center gap-1.5 font-medium truncate"
                      >
                        <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{anchor.contactName}</span>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground italic text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium max-w-[240px]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-8 w-8 rounded bg-sky-500/10 border border-sky-500/20 flex items-center justify-center shrink-0">
                        <AnchorIcon className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                      </div>
                      <span className="truncate font-semibold group-hover:text-primary transition-colors">
                        {anchor.label}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[320px] lg:max-w-[450px]">
                    <div className="flex items-center min-w-0">
                      <a
                        href={anchor.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary hover:underline min-w-0 max-w-full"
                        title={anchor.url}
                      >
                        <span className="truncate">{anchor.url}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    </div>
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell className="text-right w-[70px]" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
                            onClick={() => handleDelete(anchor)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Anchor
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </main>
  );
}
